/**
 * Schedule View — pure layout maths.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. The node-tests execute it verbatim (they strip `export ` and eval
 * the source), so a single `import` breaks the suite. It is also why every helper here is pure:
 * no Vue, no store, no Date.now() except where a clock is passed in.
 *
 * Everything the grid needs to turn a list of appointments into boxes: the day's hour window, the
 * vertical geometry of a block, how many blocks share a chair at the same moment, and how much
 * text a block is tall enough to show.
 */

/** Minutes in a day, for clamping. */
const MINUTES_PER_DAY = 1440;

/** The hours the grid falls back to when a day has nothing to derive a window from. */
export const DEFAULT_START_HOUR = 7;
export const DEFAULT_END_HOUR = 18;

/**
 * An appointment with no usable Pattern still has to occupy the chair for SOME time, or it would
 * be invisible and would not participate in overlap detection. Five minutes is one OD slot cell.
 */
export const MIN_APPOINTMENT_MINUTES = 5;

/**
 * Minutes from midnight for an Open Dental datetime string.
 *
 * The API returns OD datetimes as STRINGS (`dateStrings: true`), e.g. "2026-09-19 08:20:00", and
 * that is deliberate: these are WALL-CLOCK times in the practice's own timezone. Parsing them into
 * a JS Date would reinterpret them in the VIEWER's timezone and slide every appointment by the
 * offset — the classic "the schedule is three hours off for the west-coast office" bug. So this
 * reads the clock field textually and never constructs a Date.
 *
 * @returns {number|null} minutes from midnight, or null if unparseable
 */
export function minutesFromMidnight(odDateTime) {
  if (typeof odDateTime !== 'string') return null;
  const m = odDateTime.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null;
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
}

/** "HH:MM:SS" (OD `schedule.StartTime`) to minutes from midnight. Same no-Date rule. */
export function timeToMinutes(odTime) {
  return minutesFromMidnight(odTime);
}

/** The minutes an appointment occupies, never zero. */
export function appointmentLength(appt) {
  const len = Number(appt && appt.AptLength);
  if (!Number.isFinite(len) || len <= 0) return MIN_APPOINTMENT_MINUTES;
  return len;
}

/** Start/end minutes for an appointment, or null when it has no usable time. */
export function appointmentSpan(appt) {
  const start = minutesFromMidnight(appt && appt.AptDateTime);
  if (start === null) return null;
  const end = Math.min(MINUTES_PER_DAY, start + appointmentLength(appt));
  return { start, end };
}

/**
 * The hour window the grid draws.
 *
 * Derived from what the day actually contains — appointments, provider hours and blockouts —
 * rather than a fixed 7am-6pm, because an office that starts at 6am would otherwise have its first
 * hour cut off, and one that finishes at 2pm would stare at four empty hours. The default window
 * only applies to a day with nothing in it at all.
 *
 * Both ends are rounded OUT to a whole hour so the grid's hour rows line up with the labels, and
 * the window is widened by an hour at each end only when something actually reaches the edge.
 */
export function computeDayWindow({ appointments = [], providerHours = [], blockouts = [] } = {}) {
  let min = null;
  let max = null;
  const consider = (startMin, endMin) => {
    if (startMin === null || startMin === undefined) return;
    if (min === null || startMin < min) min = startMin;
    const e = (endMin === null || endMin === undefined) ? startMin : endMin;
    if (max === null || e > max) max = e;
  };

  appointments.forEach((a) => {
    const span = appointmentSpan(a);
    if (span) consider(span.start, span.end);
  });
  [providerHours, blockouts].forEach((rows) => {
    (rows || []).forEach((r) => consider(timeToMinutes(r.StartTime), timeToMinutes(r.StopTime)));
  });

  if (min === null || max === null) {
    return { startHour: DEFAULT_START_HOUR, endHour: DEFAULT_END_HOUR };
  }

  let startHour = Math.floor(min / 60);
  let endHour = Math.ceil(max / 60);
  // A zero-length or single-instant day would otherwise produce a grid with no height at all.
  if (endHour <= startHour) endHour = startHour + 1;
  startHour = Math.max(0, startHour);
  endHour = Math.min(24, endHour);
  return { startHour, endHour };
}

/**
 * Vertical geometry of a block, in pixels from the top of the grid body.
 *
 * `minHeight` keeps a 5-minute appointment clickable: at 76px/hour it would otherwise be 6px tall,
 * which is not a touch target. The block then overlaps its neighbour slightly, which is the right
 * trade — an unreachable appointment is worse than an imprecise one.
 */
export function blockGeometry(appt, { startHour, hourHeight, minHeight = 22 }) {
  const span = appointmentSpan(appt);
  if (!span) return null;
  const perMinute = hourHeight / 60;
  const top = (span.start - startHour * 60) * perMinute;
  const rawHeight = (span.end - span.start) * perMinute;
  return { top, height: Math.max(minHeight, rawHeight) };
}

/**
 * How much a block is tall enough to say.
 *
 * Thresholds come from the design (normal >= 56px, compact 40-56, tiny < 40). The point is that a
 * 20-minute block must not try to render two lines of procedures and clip them mid-word — it drops
 * to name + alerts, which is what the office needs to read at a glance anyway.
 */
export function densityTier(height) {
  if (!Number.isFinite(height)) return 'normal';
  if (height < 40) return 'tiny';
  if (height < 56) return 'compact';
  return 'normal';
}

/**
 * Side-by-side lanes for appointments that share one chair at the same time.
 *
 * Open Dental happily double-books an operatory and draws the overlapping appointments side by
 * side; the Claude Design export never showed this case, but a real schedule hits it constantly
 * (a hygiene check dropped into the dentist's column, a squeeze-in). Without lanes the two blocks
 * sit exactly on top of each other and one patient is invisible.
 *
 * Lanes are computed per CLUSTER of mutually-overlapping appointments, not per day: otherwise a
 * single double-booking at 8am would halve the width of every block until 5pm.
 *
 * @returns {Array} the same appointments, each with { lane, laneCount } added
 */
export function assignLanes(appointments) {
  const withSpans = (appointments || [])
    .map((appt) => ({ appt, span: appointmentSpan(appt) }))
    .filter((x) => x.span !== null)
    .sort((a, b) => (a.span.start - b.span.start) || (a.span.end - b.span.end));

  const out = [];
  let cluster = [];
  let clusterEnd = -1;

  const flush = () => {
    if (cluster.length === 0) return;
    // Greedy interval partitioning: reuse the first lane that is free by this start time.
    const laneEnds = [];
    cluster.forEach((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.span.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.span.end);
      } else {
        laneEnds[lane] = item.span.end;
      }
      item.lane = lane;
    });
    const laneCount = laneEnds.length;
    cluster.forEach((item) => {
      out.push({ ...item.appt, lane: item.lane, laneCount });
    });
    cluster = [];
    clusterEnd = -1;
  };

  withSpans.forEach((item) => {
    if (cluster.length > 0 && item.span.start >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.span.end);
  });
  flush();

  return out;
}

/**
 * Group the day's appointments into the operatory columns, with lanes applied.
 *
 * Appointments whose operatory is not among the visible columns are NOT dropped: they come back in
 * `unplaced`. That happens for real — a broken appointment can carry Op=0, and an operatory can be
 * hidden in OD after appointments were booked in it — and silently swallowing them would mean the
 * schedule quietly disagrees with Open Dental, which is the one thing this page must never do.
 */
export function groupByOperatory(appointments, operatories) {
  const columns = {};
  (operatories || []).forEach((op) => { columns[op.OperatoryNum] = []; });

  const unplaced = [];
  (appointments || []).forEach((appt) => {
    const key = appt && appt.Op;
    if (Object.prototype.hasOwnProperty.call(columns, key)) columns[key].push(appt);
    else unplaced.push(appt);
  });

  Object.keys(columns).forEach((key) => { columns[key] = assignLanes(columns[key]); });
  return { columns, unplaced };
}

/**
 * Where the "now" line sits, or null when the viewed day is not today or now is outside the window.
 *
 * `nowMinutes` is passed in rather than read from the clock here: the office's "now" is the time in
 * the PRACTICE's timezone, which the page resolves with moment-timezone from GENERAL_TIMEZONE. A
 * bare new Date() in this file would put the line at the viewer's local time instead.
 */
export function nowLineTop({ isToday, nowMinutes, startHour, endHour, hourHeight }) {
  if (!isToday) return null;
  if (!Number.isFinite(nowMinutes)) return null;
  if (nowMinutes < startHour * 60 || nowMinutes > endHour * 60) return null;
  return (nowMinutes - startHour * 60) * (hourHeight / 60);
}

/** The hour labels down the time gutter, as whole hours in the window. */
export function hourRows(startHour, endHour) {
  const rows = [];
  for (let h = startHour; h < endHour; h += 1) rows.push(h);
  return rows;
}

/** "8:20 am" from minutes past midnight, without constructing a Date. */
export function formatMinutes(mins) {
  if (!Number.isFinite(mins)) return '';
  const h24 = Math.floor(mins / 60) % 24;
  const m = Math.floor(mins % 60);
  const suffix = h24 < 12 ? 'am' : 'pm';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/**
 * The checklist card's width, needed to work out whether it fits before placing it. 368 is the
 * design's (hovercard.css `.hc`); it was 280 while the card was only a tooltip. The card's HEIGHT is
 * never assumed any more: the layer measures it after it renders and passes it to hoverCardPosition.
 */
export const HOVER_CARD_WIDTH = 368;
export const HOVER_CARD_GAP = 8;
/** Keep the card off the very edge of the window — a card flush to the glass reads as clipped. */
export const HOVER_CARD_MARGIN = 8;

/**
 * Where to put the appointment hover card, in VIEWPORT coordinates.
 *
 * The card is `position: fixed` because the grid clips its columns, and the blocks most likely to
 * need a card are the crowded ones near a column edge — a normally-positioned popover would be cut
 * in half exactly where it matters. Fixed means this has to do the flipping that a containing
 * block would otherwise handle.
 *
 * Two flips, each for a real case rather than a hypothetical one:
 *   • horizontal — the LAST operatory column sits against the right edge of the window, so a card
 *     opening rightwards would hang off-screen. It opens leftwards instead.
 *   • vertical — an appointment late in the day sits near the bottom of the viewport, so a card
 *     aligned to its top would run past the fold. It is lifted just enough to fit.
 *
 * Returned as a style object rather than applied here, so the decision is pure and testable
 * without a DOM: `layout.js` is executed verbatim by the node-tests and must stay import-free.
 *
 * @param {{top:number,left:number,right:number,bottom:number}} rect  the block's getBoundingClientRect
 * @param {{width:number,height:number}} viewport
 * @param {number} cardHeight  the card's measured height, or an estimate before it is rendered
 */
export function hoverCardPosition(rect, viewport, cardHeight) {
  const vw = (viewport && viewport.width) || 0;
  const vh = (viewport && viewport.height) || 0;
  const h = Number.isFinite(cardHeight) ? cardHeight : 0;

  // Prefer the right of the block; flip when the card would not fit there.
  let left = rect.right + HOVER_CARD_GAP;
  const fitsRight = left + HOVER_CARD_WIDTH + HOVER_CARD_MARGIN <= vw;
  if (!fitsRight) {
    left = rect.left - HOVER_CARD_GAP - HOVER_CARD_WIDTH;
  }
  // No room on either side (a narrow window, the phone timeline driven by a mouse, an iPad in
  // portrait): below or above the block instead. Never ON it — see stackedCardPosition.
  if (!fitsRight && left < HOVER_CARD_MARGIN) {
    const stacked = stackedCardPosition(rect, vw, vh, h);
    if (stacked) return stacked;
  }
  // Nowhere to go at all: clamp rather than choosing an off-screen edge.
  if (left < HOVER_CARD_MARGIN) left = HOVER_CARD_MARGIN;

  // Align to the block's top, then lift it if that would run past the bottom of the window.
  let top = rect.top;
  if (top + h + HOVER_CARD_MARGIN > vh) top = vh - h - HOVER_CARD_MARGIN;
  if (top < HOVER_CARD_MARGIN) top = HOVER_CARD_MARGIN;

  return {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${HOVER_CARD_WIDTH}px`,
  };
}

/** Below this, a space above or below a block is not somewhere a card can live. */
export const HOVER_CARD_MIN_HEIGHT = 160;

/**
 * The card BELOW its block, or above it — for a block with no room on either side.
 *
 * Found on the real page (2026-10-12). The old answer clamped the card to the left margin at the
 * block's own top, so it opened ON the block it describes, under the resting pointer, 300 ms after
 * the pointer arrived: a click meant for the block landed on a checkbox, and a tick is shared with
 * the whole office.
 *
 * Below when the card fits there, else above when it fits there, else whichever space is larger with
 * the card CAPPED to it (its body scrolls). The space is always returned as `maxHeight`, for two
 * reasons: a card that grows later (a live refresh adds a line) cannot spill onto the block, and the
 * answer is stable — the layer re-measures a capped card at its cap, and that height must choose the
 * same spot again or the card would jump.
 *
 * Returns null when neither space can hold a card (a very short window); the caller clamps.
 */
function stackedCardPosition(rect, vw, vh, h) {
  const below = vh - HOVER_CARD_MARGIN - (rect.bottom + HOVER_CARD_GAP);
  const above = rect.top - HOVER_CARD_GAP - HOVER_CARD_MARGIN;
  let top;
  let space;
  if (h <= below) {
    top = rect.bottom + HOVER_CARD_GAP;
    space = below;
  } else if (h <= above) {
    top = rect.top - HOVER_CARD_GAP - h;
    space = above;
  } else if (Math.max(below, above) < HOVER_CARD_MIN_HEIGHT) {
    return null;
  } else if (below >= above) {
    top = rect.bottom + HOVER_CARD_GAP;
    space = below;
  } else {
    top = HOVER_CARD_MARGIN;
    space = above;
  }

  // From the block's own left edge, pulled back inside the window.
  let left = Math.min(rect.left, vw - HOVER_CARD_WIDTH - HOVER_CARD_MARGIN);
  if (left < HOVER_CARD_MARGIN) left = HOVER_CARD_MARGIN;

  return {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${HOVER_CARD_WIDTH}px`,
    maxHeight: `${Math.round(space)}px`,
  };
}

// ── Shared by the grid and the phone timeline ──────────────────────────────────────────────────

/** "8 am" — the hour label for the grid's time gutter and the timeline's rail alike. */
export function hourLabel(hour) {
  return formatMinutes(hour * 60).replace(':00', '');
}

/**
 * "8:00 – 9:00 am", or "11:30 am – 12:30 pm" when the range crosses noon. The phone card is
 * narrow, and a suffix said twice is the first thing worth dropping.
 */
export function formatTimeRange(start, end) {
  if (!Number.isFinite(start)) return '';
  const from = formatMinutes(start);
  if (!Number.isFinite(end)) return from;
  const to = formatMinutes(end);
  if (from.slice(-2) === to.slice(-2)) return `${from.slice(0, -3)} – ${to}`;
  return `${from} – ${to}`;
}

/**
 * What the office calls the patient: `LName, FName`, always.
 *
 * ⛔ The PREFERRED name is never shown (Nilay, 2026-10-23: a free-text nickname put "Shah,
 * edited71126" on the schedule). The patient field keeps carrying `Preferred` — the Patient Panel
 * header is shared with the Patient Profile page and shows it by its own rule — but nothing on
 * Schedule View reads it.
 *
 * One definition for the grid block, the phone card, the table row and the checklist card title,
 * so no two surfaces can name the same person differently.
 */
export function patientDisplayName(appt) {
  const p = (appt && appt.patient) || {};
  const first = p.FName || '';
  const last = p.LName || '';
  if (last && first) return `${last}, ${first}`;
  return last || first || 'No patient';
}

/** Two letters for the phone card's avatar, first name then surname: "CD" for Colette Danielson. */
export function patientInitials(appt) {
  const p = (appt && appt.patient) || {};
  const first = String(p.FName || '').trim();
  const last = String(p.LName || '').trim();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
}

/**
 * The provider a phone card or table row names — and whose Open Dental colour is the block's stripe
 * (provColor.js providerStripeColor): the hygienist for a hygiene visit, otherwise the dentist.
 * Null when the appointment carries neither.
 */
export function appointmentProviderNum(appt) {
  if (!appt) return null;
  const prov = Number(appt.ProvNum);
  const hyg = Number(appt.ProvHyg);
  if (Number(appt.IsHygiene) === 1 && hyg > 0) return hyg;
  if (prov > 0) return prov;
  return hyg > 0 ? hyg : null;
}

// ⛔ There is no operatory colour palette any more (Nilay, 2026-10-23): the columns are white and a
// chair is told apart by its name. The one colour on a block is the PROVIDER's Open Dental
// "Appointment Color", the stripe — see provColor.js. Do not reintroduce a per-chair tint or accent.

// ── Procedure text ─────────────────────────────────────────────────────────────────────────────

/**
 * A procedure description cut into its CODES and the words around them, so a block can set the
 * code ("D2950") a weight heavier than the description ("Core buildup #19") — the design's
 * `.procedures .code`. `ProcDescript` is one string ("D2950 Core buildup #19, D2740 Crown - porcelain"),
 * so the codes are found by SHAPE: a capital letter and exactly four digits, standing alone.
 *
 * ⛔ LOSSLESS: the parts' `text` always joins back to exactly the string it was given. This only
 * styles; it must never be the reason a word goes missing from a block. Not text, or empty, is no
 * parts (never a throw, never "undefined").
 *
 * @returns {Array<{ text: string, code: boolean }>}
 */
export function procedureParts(text) {
  if (typeof text !== 'string' || !text) return [];
  const parts = [];
  const re = /\b[A-Z]\d{4}\b/g;
  let last = 0;
  let m = re.exec(text);
  while (m) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), code: false });
    parts.push({ text: m[0], code: true });
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) parts.push({ text: text.slice(last), code: false });
  return parts;
}

// ── The grid's columns (and the phone's "Columns" view) ────────────────────────────────────────

/**
 * The grid's column geometry, in CSS px: one time gutter plus one column per chair, each column AT
 * LEAST `GRID_OP_COL_MIN` wide and sharing the rest. On a phone (the Columns view, WP-B 2026-10-23)
 * a column may be a little narrower, so about two chairs show across a 390px screen while the rest
 * scroll sideways under a sticky gutter and header.
 *
 * ⛔ `$sv-time-col`, `$sv-op-col-min` and `$sv-op-col-min-phone` in _tokens.scss are these same
 * numbers; a node-test (schedule-view-layout.test.js) holds the two together.
 */
export const GRID_TIME_COL = 56;
export const GRID_OP_COL_MIN = 180;
export const GRID_OP_COL_MIN_PHONE = 165;

/**
 * The grid's `grid-template-columns`. No chairs (or a count that is not a positive number) is the
 * gutter alone: `repeat(0, …)` is not a valid track list, and an invalid declaration is dropped
 * whole — the grid would lose its gutter too.
 */
export function gridTemplateColumns(operatoryCount, isPhone) {
  const count = Math.floor(Number(operatoryCount));
  if (!Number.isFinite(count) || count < 1) return `${GRID_TIME_COL}px`;
  const min = isPhone ? GRID_OP_COL_MIN_PHONE : GRID_OP_COL_MIN;
  return `${GRID_TIME_COL}px repeat(${count}, minmax(${min}px, 1fr))`;
}

// ── The phone timeline (Phase 6) ───────────────────────────────────────────────────────────────

/**
 * The widest window that gets the phone timeline instead of the operatory grid, in CSS px.
 *
 * 600, not the header's 768: an iPad mini held upright is 744px wide, and the iPad is the
 * office's schedule screen — it keeps the grid. Every phone held upright is at most 430px. A phone
 * turned sideways (667–932px) gets the grid too, which is what a wide, short screen is for.
 *
 * ⛔ `$sv-phone-max` in _tokens.scss must be the same number. This decides WHICH view renders; the
 * stylesheet decides whether the popovers become bottom sheets, and a gap between the two would
 * give a timeline with desktop popovers hanging off a 390px screen. A node-test holds them together.
 */
export const PHONE_MAX_WIDTH = 599.98;
export const PHONE_MEDIA_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;

/** Same-start ordering inside an hour: a blockout ("Lunch") reads before the people in it. */
const TIMELINE_KIND_ORDER = { blockout: 0, appt: 1 };

/**
 * The day as one column, for a phone: every chair's appointments interleaved by start time and
 * grouped under the hour they START in, each card naming its chair.
 *
 * Returns `{ hours, unplaced, appointmentCount }`:
 *   • hours — `[{ hour, label, state, items }]`, only hours that have something in them, in order.
 *     `state` is 'past' / 'current' when viewing today, else null. Items are
 *       { kind: 'appt', key, start, end, appt }
 *       { kind: 'blockout', key, start, end, name, allChairs, operatoryNums }
 *       { kind: 'now', key, start }
 *   • unplaced — appointments that cannot be put on the timeline. Same rule as the grid's
 *     `groupByOperatory`: a chair that is not on screen is NOT silently dropped, it is counted, so
 *     a phone and an iPad showing the same filter always agree about who is missing and why.
 *
 * The NOW marker goes after everything that has already started, so an appointment in progress
 * sits above it. It is added even when now falls in an hour with nothing booked — before the
 * first patient or after the last, "now" is still the most useful line on the page.
 *
 * Order within an hour is fully determined (start, then blockouts first, then chair order, then
 * AptNum), so a live refresh that changes nothing never shuffles the cards under somebody's thumb.
 */
export function buildTimeline({
  appointments = [], operatories = [], blockouts = [], isToday = false, nowMinutes = null,
} = {}) {
  const column = {};
  (operatories || []).forEach((op, i) => { if (op) column[op.OperatoryNum] = i; });
  const isOnScreen = opNum => Object.prototype.hasOwnProperty.call(column, opNum);

  const entries = [];
  const unplaced = [];
  (appointments || []).forEach((appt) => {
    if (!appt) return;
    const span = appointmentSpan(appt);
    if (!span || !isOnScreen(appt.Op)) {
      unplaced.push(appt);
      return;
    }
    entries.push({
      kind: 'appt', key: `a-${appt.AptNum}`, start: span.start, end: span.end,
      column: column[appt.Op], aptNum: Number(appt.AptNum) || 0, appt,
    });
  });
  const appointmentCount = entries.length;

  // One blockout in Open Dental spans several chairs as one schedule row plus a scheduleop row
  // per chair, which arrives here as one entry per chair. The grid draws each in its column; a
  // single column has to fold them back into ONE line or "Lunch" would be listed five times.
  const byBlockout = {};
  (blockouts || []).forEach((b) => {
    if (!b) return;
    const start = timeToMinutes(b.StartTime);
    const end = timeToMinutes(b.StopTime);
    if (start === null || end === null || end <= start) return;
    // No scheduleop row = every chair (lunch, a staff meeting). Same rule as the grid.
    const everyChair = b.OperatoryNum === null || b.OperatoryNum === undefined;
    if (!everyChair && !isOnScreen(b.OperatoryNum)) return;
    const id = (b.ScheduleNum !== null && b.ScheduleNum !== undefined)
      ? String(b.ScheduleNum)
      : `${start}-${end}-${b.BlockoutName || ''}`;
    let entry = byBlockout[id];
    if (!entry) {
      entry = {
        kind: 'blockout', key: `b-${id}`, start, end, column: Infinity, aptNum: 0,
        name: b.BlockoutName || '', allChairs: false, operatoryNums: [],
      };
      byBlockout[id] = entry;
      entries.push(entry);
    }
    if (everyChair) {
      entry.allChairs = true;
      entry.column = -1;
    } else {
      if (entry.operatoryNums.indexOf(b.OperatoryNum) === -1) entry.operatoryNums.push(b.OperatoryNum);
      entry.column = Math.min(entry.column, column[b.OperatoryNum]);
    }
  });
  const chairCount = (operatories || []).length;
  Object.keys(byBlockout).forEach((id) => {
    const entry = byBlockout[id];
    entry.operatoryNums.sort((a, b) => column[a] - column[b]);
    // Named on every chair on screen is "all chairs", whichever way Open Dental stored it.
    if (chairCount > 1 && entry.operatoryNums.length === chairCount) entry.allChairs = true;
    if (entry.allChairs) entry.operatoryNums = [];
  });

  entries.sort((a, b) => (a.start - b.start)
    || (TIMELINE_KIND_ORDER[a.kind] - TIMELINE_KIND_ORDER[b.kind])
    || (a.column - b.column)
    || (a.aptNum - b.aptNum));

  const byHour = {};
  const hours = [];
  const hourFor = (hour) => {
    if (!byHour[hour]) {
      byHour[hour] = { hour, label: hourLabel(hour), state: null, items: [] };
      hours.push(byHour[hour]);
    }
    return byHour[hour];
  };
  entries.forEach((e) => {
    // The sort keys are internal; the component gets the item without them.
    const item = Object.assign({}, e);
    delete item.column;
    delete item.aptNum;
    hourFor(Math.floor(e.start / 60)).items.push(item);
  });

  const showNow = !!isToday && Number.isFinite(nowMinutes)
    && nowMinutes >= 0 && nowMinutes < MINUTES_PER_DAY;
  if (showNow) {
    const group = hourFor(Math.floor(nowMinutes / 60));
    let at = group.items.findIndex(item => item.start > nowMinutes);
    if (at === -1) at = group.items.length;
    group.items.splice(at, 0, { kind: 'now', key: 'now', start: nowMinutes });
  }

  hours.sort((a, b) => a.hour - b.hour);
  if (showNow) {
    hours.forEach((g) => {
      if ((g.hour + 1) * 60 <= nowMinutes) g.state = 'past';
      else if (g.hour * 60 <= nowMinutes) g.state = 'current';
    });
  }

  return { hours, unplaced, appointmentCount };
}
